<template>
    <div ref="paymentMethodSelectorElement" class="payment-method-selector-component"></div>
</template>
<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { PaymentMethodSelectorMode } from "@/app/components/payment/PaymentMethodSelectorMode";
    import { PaymentMethodSelector } from "@/app/elements/PaymentMethodSelector";
    import { globalStores } from "@/app/globalStores";
    import type { IPaymentMethod } from "@/data/model/Customer";
    import { logError } from "@/shared/logging/errors";
    import { type Ref, computed, onMounted, ref, watch } from "vue";

    const props = defineProps<{
        totalConsumption: Ref<number>;
        discountAmount: Ref<number>;
        tip: Ref<number | null>;
        hasVouchers: Ref<boolean>;
        isPayService: Ref<boolean>;
        setApplyDiscountSectionVisibility: () => void;
        onModeChange: (mode: PaymentMethodSelectorMode) => void;
        onOpenClose: (isOpen: boolean) => void;
    }>();

    const paymentMethodSelectorElement = ref<HTMLElement | null>(null);
    const isOpen = ref(false);
    const paymentMethodSelectorMode = ref<PaymentMethodSelectorMode>(PaymentMethodSelectorMode.Default);
    const isTotalFullyCoveredByDiscounts = computed(
        () =>
            props.discountAmount.value > 0 &&
            props.totalConsumption.value == props.discountAmount.value &&
            props.tip.value == 0
    );
    const discountsPaymentMethodObservable = computed(
        () => `${isTotalFullyCoveredByDiscounts.value}_${props.hasVouchers.value}`
    );

    let methodSelector: PaymentMethodSelector | null = null;
    onMounted(() => {
        methodSelector = new PaymentMethodSelector().appendTo($(paymentMethodSelectorElement.value!));
        methodSelector.onCardFormDestroyed = () => {
            TastierClient.payContext.clearPaymentMethod();
        };

        methodSelector.onSelect = (method, withCardToken) => {
            if (method == null) {
                TastierClient.payContext.clearPaymentMethod();
                props.setApplyDiscountSectionVisibility();
            } else {
                if (method.type === "OpenCashDrawer") {
                    return;
                } else {
                    const handler = TastierClient.payContext.setPaymentMethod(method, withCardToken);
                    props.setApplyDiscountSectionVisibility();
                    return handler;
                }
            }
        };

        methodSelector.onModeChange = (newIsCardFormMode: PaymentMethodSelectorMode) => {
            paymentMethodSelectorMode.value = newIsCardFormMode;
            props.onModeChange(newIsCardFormMode);
        };
        methodSelector.onOpenClose = (newIsOpen) => {
            isOpen.value = methodSelector?.isOpen.value ?? false;
            props.onOpenClose(newIsOpen);
        };
    });

    async function loadPaymentIntent() {
        methodSelector!.setLoading();
        try {
            await TastierClient.payContext.prepareForCheckout();
        } catch (ex) {
            logError(ex);
        }
    }

    watch(discountsPaymentMethodObservable, async () => {
        if (isTotalFullyCoveredByDiscounts.value) {
            if (props.hasVouchers.value) {
                await methodSelector!.setAvailableOptions([globalStores.paymentStore!.vouchersPaymentMethod], true);
            } else {
                await methodSelector!.setAvailableOptions([globalStores.paymentStore!.discountsPaymentMethod], true);
            }
        } else {
            methodSelector!.reset();
            await methodSelector!.setAvailableOptions([]);

            // only refresh if cart or bill are set
            if (!props.isPayService.value) {
                if (TastierClient.context.getCurrentCart() != null) {
                    await loadPaymentIntent();
                } else {
                    console.log("Skip payment intent refresh - no cart");
                }
            } else if (props.isPayService.value) {
                if (TastierClient.context.currentBill != null) {
                    await loadPaymentIntent();
                } else {
                    console.log("Skip payment intent refresh - no bill");
                }
            }
        }
    });

    async function setAvailableOptions(availableOptions: IPaymentMethod[]) {
        if (isTotalFullyCoveredByDiscounts.value) {
            if (props.hasVouchers.value) {
                await methodSelector!.setAvailableOptions([globalStores.paymentStore!.vouchersPaymentMethod], true);
            } else {
                await methodSelector!.setAvailableOptions([globalStores.paymentStore!.discountsPaymentMethod], true);
            }
        } else {
            await methodSelector!.setAvailableOptions(availableOptions);
        }
    }

    defineExpose({
        isOpen,
        paymentMethodSelectorMode,
        reset: () => methodSelector!.reset(),
        open: () => methodSelector!.open(),
        close: () => methodSelector!.close(),
        validateCardForm: () => methodSelector!.validateCardForm(),
        setLoading: () => methodSelector!.setLoading(),
        setPaymentInProgress: (paymentInProgress: boolean) => (methodSelector!.paymentInProgress = paymentInProgress),
        setAvailableOptions,
    });
</script>
